import { NavLink } from "@/project/karuna/components/NavLink"; // @ts-ignore - NavLink is not a valid module
import { LayoutDashboard, Calendar, FileText, User, Users } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/project/karuna/components/ui/sidebar"; // @ts-ignore - sidebar is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { getUserInfo } from "@/lib/storeManager";
import {
  fetchSchoolApprovalForCurrentUser,
  normalizeSchoolMobile,
  readCachedSchoolApprovalForPhone,
} from "@/project/karuna/utils/schoolProfileApproval";

const menuItems = [
  { title: "Dashboard", url: "/school/dashboard", icon: LayoutDashboard },
  { title: "Activities", url: "/school/activities", icon: Calendar },
  { title: "My Submissions", url: "/school/submissions", icon: FileText },
  // { title: "Submit Report", url: "/school/activities", icon: Upload },
  { title: "Students", url: "/school/students", icon: Users },
  { title: "Profile", url: "/school/profile", icon: User },
];

function getInitialSidebarApprovalState(): {
  approved: boolean;
  verifying: boolean;
} {
  const phone = normalizeSchoolMobile(getUserInfo()?.phone_number ?? "");
  if (!phone) {
    return { approved: false, verifying: true };
  }
  const cached = readCachedSchoolApprovalForPhone(phone);
  if (cached === true) {
    return { approved: true, verifying: false };
  }
  if (cached === false) {
    return { approved: false, verifying: false };
  }
  return { approved: false, verifying: true };
}

export function SchoolSidebar() {
  const { open } = useSidebar();
  const getLink = useKarunaLink();
  const navigate = useNavigate();
  const location = useLocation();
  const initial = getInitialSidebarApprovalState();
  const [isApproved, setIsApproved] = useState(initial.approved);
  /** True only while we have no cached answer yet (unknown) — not while re-fetching an approved school */
  const [isVerifying, setIsVerifying] = useState(initial.verifying);
  /** Only the first mount uses showLoading path; route changes refresh in background */
  const isInitialApprovalLoad = useRef(true);

  const refreshApproval = useCallback(
    async (opts?: { showLoading?: boolean }) => {
      const showLoading = opts?.showLoading ?? false;
      const phone = normalizeSchoolMobile(getUserInfo()?.phone_number ?? "");
      const cached = readCachedSchoolApprovalForPhone(phone);
      // Only show "verifying" when we have no cached answer yet (unknown), not when cached false/true
      const shouldBlockNav = showLoading && cached !== true && cached !== false;

      if (shouldBlockNav) {
        setIsVerifying(true);
      }
      try {
        const { approved } = await fetchSchoolApprovalForCurrentUser();
        setIsApproved(approved);
      } catch (e) {
        console.error("❌ Error checking approval status:", e);
        setIsApproved(false);
      } finally {
        if (shouldBlockNav) {
          setIsVerifying(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    const showLoading = isInitialApprovalLoad.current;
    if (isInitialApprovalLoad.current) {
      isInitialApprovalLoad.current = false;
    }
    void refreshApproval({ showLoading });
  }, [location.pathname, refreshApproval]);

  // After admin approves, school tab can refresh without reload
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshApproval({ showLoading: false });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshApproval]);

  const handleMenuItemClick = (
    item: (typeof menuItems)[0],
    e: React.MouseEvent
  ) => {
    // If profile is not approved and item is not Profile, prevent navigation
    if (!isApproved && item.title !== "Profile") {
      e.preventDefault();
      e.stopPropagation();
      // Redirect to profile page
      navigate(getLink("/school/profile"));
    }
  };

  return (
    <Sidebar>
      <SidebarTrigger className="m-2 self-end" />

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>School Portal</SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map(item => {
                // Disable all tabs except Profile if:
                // 1. Still loading approval status, OR
                // 2. Profile is not approved
                // Profile is always enabled
                // Note: isProfileCompleted is checked by SchoolLayout, so we only check isApproved here
                const isDisabled =
                  (isVerifying || !isApproved) && item.title !== "Profile";

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild={!isDisabled}
                      disabled={isDisabled}
                      className={
                        isDisabled ? "cursor-not-allowed opacity-50" : ""
                      }
                    >
                      {isDisabled ? (
                        <div
                          onClick={e => handleMenuItemClick(item, e)}
                          className="flex w-full cursor-not-allowed items-center px-2 py-2 text-sm font-medium text-muted-foreground"
                          title={
                            isVerifying
                              ? "Loading..."
                              : "Please wait for profile approval"
                          }
                        >
                          <item.icon className="mr-2 h-4 w-4" />
                          {open && <span>{item.title}</span>}
                        </div>
                      ) : (
                        <NavLink
                          to={getLink(item.url)}
                          end
                          className="hover:bg-muted/50"
                          activeClassName="bg-muted text-primary font-medium"
                        >
                          <item.icon className="mr-2 h-4 w-4" />
                          {open && <span>{item.title}</span>}
                        </NavLink>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
